import { pageSchema } from "@/helpers/types";
import { z } from "zod";

// Alert status enum
export enum AlertStatus {
  ACTIVE = "active",
  INACTIVE = "inactive",
}

// Notification channel type enum
export enum NotificationChannelType {
  EMAIL = "email",
  SLACK = "slack",
}

// Entity type enum
export enum EntityType {
  ACCOUNT = "account",
  WAREHOUSE = "warehouse",
  DATABASE = "database",
  TABLE = "table",
  USER = "user",
  ROLE = "role",
  QUERY = "query",
  AI_SERVICE = "ai_service",
}

export const AlertEventSchema = z.object({
  id: z.coerce.string(),
  created_at: z.string(),
});

// Entity config schemas
const EntityConfigSchema = z.object({
  type: z.nativeEnum(EntityType),
  qualified_name: z.string(),
});

// Metric config schemas
const MetricConfigSchema = z.object({
  db_type: z.string(),
  context_query: z.string(),
  trigger_query: z.string(),
});

// Schedule config schemas
const ScheduleConfigSchema = z.object({
  cron: z.string(),
  timezone: z.string(),
});

// Notifier config schemas
const EmailConfigSchema = z.object({
  emails: z.array(z.string()),
});

const SlackConfigSchema = z.object({
  ids: z.array(z.number()),
});

const NotifierConfigSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal(NotificationChannelType.EMAIL),
    email_config: EmailConfigSchema,
  }),
  z.object({
    type: z.literal(NotificationChannelType.SLACK),
    slack_config: SlackConfigSchema,
  }),
]);

export type NotifierConfig = z.infer<typeof NotifierConfigSchema>;

export const AlertSchema = z.object({
  id: z.coerce.string(),
  name: z.string(),
  description: z.string(),
  alert_type: z.string(),
  entity_type: z.nativeEnum(EntityType),
  entity_config: EntityConfigSchema.nullable(),
  threshold: z.number(),
  period: z.number(),
  metric_config: MetricConfigSchema,
  schedule_config: ScheduleConfigSchema.nullable(),
  notifier_config: z.array(NotifierConfigSchema),
  alert_message: z.string(),
  status: z.nativeEnum(AlertStatus),
  created_at: z.string(),
  updated_at: z.string(),
  created_by: z.number(),
  updated_by: z.number(),
  alert_events: z.array(AlertEventSchema).optional().default([]),
  no_of_alerts: z.number().optional().nullable().default(0),
  last_alert: z.string().nullable().optional(),
});

export type Alert = z.infer<typeof AlertSchema>;

// Notification schemas
export const AlertNotificationSchema = z.object({
  id: z.coerce.string(),
  alert_id: z.union([z.string(), z.number()]),
  alert: z
    .object({
      name: z.string(),
    })
    .optional(),
  created_at: z.string(),
  message: z.string(),
  value: z.union([z.string(), z.number()]),
});

export type AlertNotification = z.infer<typeof AlertNotificationSchema>;

export interface NotificationTimeFilter {
  options: Array<{ value: string; label: string }>;
  value: string | null;
  onChange: (value: string) => void;
  getLabel?: () => string;
}

export interface AlertBoxProps {
  suggestionWorkflowType?: string;
}

// API Response schemas
export const AlertEventsResponseSchema = pageSchema(AlertNotificationSchema);

export type AlertEventsResponse = z.infer<typeof AlertEventsResponseSchema>;

export const AlertRulesResponseSchema = z.object({
  items: z.array(AlertSchema),
  total: z.number().optional(),
});

export type AlertRulesResponse = z.infer<typeof AlertRulesResponseSchema>;

export const SlackChannelSchema = z.object({
  id: z.coerce.string(),
  type: z.string().default("slack"),
  config: z.object({
    webhook_url: z.string(),
    name: z.string(),
  }),
});

export const SlackChannelsResponseSchema = z.array(SlackChannelSchema);

export type SlackChannelsResponse = z.infer<typeof SlackChannelsResponseSchema>;

// API Request params
export interface GetAlertEventsParams {
  page: number;
  size: number;
  alert_id?: string | number;
  timestamp?: string;
  sortColumn?: string;
  sortOrder?: string;
}

export interface GetAlertRulesParams {
  page: number;
  size: number;
}
